import React, { ChangeEvent, useContext, useEffect, useState } from 'react';

import { UserContext } from '@/routes';
import { Heading } from '@/components/Elements/Heading';
import { default as ReactSelect, components } from 'react-select';
import { PetWeight } from '@/features/payment/api/petWeights';
import { PetLimited, Pets } from '@/features/customers';

import Utils from '@/utils/util';
import { PetType } from '@/features/availability/components/PetType';
import PetWeightSelection from '@/features/availability/components/PetWeightSelection';
import './PetFieldsComponent.scss';
import { DropDown } from '@/components/dropdown/DropDown';
import { useAuth } from '@/lib/auth';
import { FileUploader } from '@/features/customers/components/FileUploader';
import { useUploadFile } from '../api/getFields';
import WebcamModal from '@/components/WebCam/WebCam';
import { showNotification } from '@/utils/notificationUtils';
type Props = {
  data?: PetLimited;
  disabled?: boolean;
  onChange: (data: PetLimited) => void;
};
export const PetFieldsComponent = ({ data, disabled, onChange }: Props) => {
  const { user } = useAuth();
  const userContext = useContext(UserContext);
  const uploadPictureMutation = useUploadFile();

  const [pet, setPet] = useState<PetLimited>();
  const defaultPetType = Utils.getPetTypes(
    user && user.offerings ? user.offerings?.petTypes : userContext?.business?.offerings?.petTypes
  );
  useEffect(() => {
    if (data) {
      setPet(data);
    } else {
      const petData = {
        name: '',
        breed: Utils.BREED_UNSPECIFIED,
        petType: defaultPetType ? defaultPetType : '',
        petSize: Utils.DEFAULT_PET_SIZE,
        color: '',
      };
      setPet(petData);
      updatePetData(petData);
    }
  }, [data]);

  const updatePetData = (petData: Pets | any) => {
    onChange({ ...petData });
  };
  const handleChangePetDetails = (event: ChangeEvent<HTMLInputElement> | any) => {
    const petData = { ...pet };
    const value = event.target.value ? event.target.value : '';
    if (event.target.id === 'name') {
      petData.name = value;
    }
    updatePetData(petData);
  };

  const onPetSizeChange = (petSize: PetWeight) => {
    const petData = { ...pet };
    petData.petSize = petSize?.key;
    updatePetData(petData);
  };

  const onPetTypeChange = (petType: string) => {
    const petData = { ...pet };
    petData.petType = petType;
    updatePetData(petData);
  };
  const onProfilePicUpload = async (formData: FormData) => {
    const response = await uploadPictureMutation.mutateAsync({
      file: formData,
    });
    const petData = { ...pet };
    petData.petsImageUrl = response ? response.fileUrl : '';
    updatePetData(petData);
  };

  const onBreedChange = (breed: string) => {
    const petData = { ...pet };
    petData.breed = breed;
    updatePetData(petData);
  };
  const onColorChange = (color: string) => {
    const petData = { ...pet };
    petData.color = color;
    updatePetData(petData);
  };
  const captureCameraImage = (file: File) => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      onProfilePicUpload(formData);
    }
  };
  const breedList = userContext?.refValues?.breeds ? userContext?.refValues?.breeds : [];
  const colorList = userContext?.refValues?.colors ? userContext?.refValues?.colors : [];
  return (
    <div className="pet-field">
      <>
        <div className="pet-row mb-3">
          <FileUploader
            key={'pet-profile-pic'}
            onUpload={onProfilePicUpload}
            url={pet?.petsImageUrl}
            isUploading={uploadPictureMutation.isLoading}
            disabled={disabled}
            hideHelperText={userContext?.config?.intakeViewMode}
          />
          {!Utils.isMobile() && !userContext?.config?.intakeViewMode && (
            <WebcamModal
              disabled={disabled || uploadPictureMutation.isLoading}
              onCapture={captureCameraImage}
            />
          )}
        </div>
        <div className="pet-row pet-type">
          <PetType
            onChange={onPetTypeChange}
            value={pet?.petType}
            readOnly={disabled}
            defaultPetType={defaultPetType}
          />
        </div>
        {userContext && userContext?.isLandscape ? (
          <div className="title-and-add-btn-row mb-3">
            <div>
              <Heading
                size="sm"
                title=""
                description="Enter name and breed of your pet"
                fontWeight="sm"
              />
            </div>
          </div>
        ) : (
          <div className="add-new-title">Enter name, breed and color of your pet</div>
        )}
        <div className="pet-row mb-4">
          <div className="pet-col-6">
            <input
              type="text"
              className={`new-cust-input ${pet?.name ? '' : 'empty'}`}
              placeholder="Pet name"
              id="name"
              onChange={handleChangePetDetails}
              value={pet?.name}
              maxLength={20}
              disabled={disabled}
            />
          </div>
          <div className="pet-col-6">
            <DropDown
              placeholder="Select Breed"
              selectedValue={pet?.breed}
              data={breedList ? breedList : []}
              onSelect={(value: any) => {
                onBreedChange(value);
              }}
              onDismiss={() => {}}
              enableSearch={true}
              isDisabled={disabled}
              customClass={pet?.breed ? '' : 'empty'}
            />
          </div>
        </div>
        <div className="pet-row mb-4">
          <div className="pet-col-6">
            <DropDown
              placeholder="Select Color"
              selectedValue={pet?.color}
              data={colorList ? colorList : []}
              onSelect={(value: any) => {
                onColorChange(value);
              }}
              onDismiss={() => {}}
              enableSearch={true}
              isDisabled={disabled}
            />
          </div>
        </div>
        <div className="booking-desc pet-size">Select your pet size</div>
        <div className="selected-pet-row">
          <PetWeightSelection
            selectedSize={pet?.petSize ? pet?.petSize : undefined}
            onChange={onPetSizeChange}
            readOnly={disabled}
          />
        </div>
      </>
    </div>
  );
};
